import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useActualizarUsuario,
  useCrearUsuario,
  useDesbloquearUsuario,
  useResetPin,
  useUsuarios,
} from '@/hooks/useUsuarios';
import type { RolUsuario, UsuarioApp } from '@adelante/shared';
import { CircleNotch, Key, Lock, LockOpen, Plus, UserPlus } from '@phosphor-icons/react';
import { useState } from 'react';
import { toast } from 'sonner';

export function UsuariosPantalla() {
  const usuariosQ = useUsuarios();
  const usuarios = usuariosQ.data?.data ?? [];

  const [modalCrear, setModalCrear] = useState(false);
  const [editar, setEditar] = useState<UsuarioApp | null>(null);
  const [resetPinDe, setResetPinDe] = useState<UsuarioApp | null>(null);

  const desbloquear = useDesbloquearUsuario();

  async function handleDesbloquear(u: UsuarioApp) {
    try {
      await desbloquear.mutateAsync(u.id);
      toast.success(`${u.nombre} desbloqueado`);
    } catch (e) {
      toast.error('Error', { description: e instanceof Error ? e.message : 'desconocido' });
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Usuarios</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Personal con acceso a la app. El PIN nunca se muestra — solo se resetea.
          </p>
        </div>
        <Button onClick={() => setModalCrear(true)}>
          <UserPlus className="mr-2 h-4 w-4" weight="bold" aria-hidden="true" />
          Nuevo usuario
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{usuarios.length} usuarios</CardTitle>
        </CardHeader>
        <CardContent>
          {usuariosQ.isLoading && (
            <div className="flex items-center gap-2 py-8 text-muted-foreground">
              <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
              Cargando…
            </div>
          )}
          {usuariosQ.error && (
            <p className="py-8 text-destructive">Error: {usuariosQ.error.message}</p>
          )}
          {!usuariosQ.isLoading && !usuariosQ.error && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead className="text-center">Estado</TableHead>
                  <TableHead>Último login</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usuarios.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.nombre}</TableCell>
                    <TableCell className="font-mono text-xs">@{u.usuario}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {u.telefono ?? '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={u.rol === 'admin' ? 'default' : 'secondary'}>{u.rol}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex flex-col items-center gap-1">
                        {u.activo ? (
                          <Badge className="bg-adelante-primary text-adelante-foreground hover:bg-adelante-primary-dark">
                            Activo
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Inactivo</Badge>
                        )}
                        {u.bloqueado && (
                          <span className="flex items-center gap-1 text-[10px] text-destructive">
                            <Lock className="h-3 w-3" weight="fill" aria-hidden="true" />
                            Bloqueado
                          </span>
                        )}
                        {!u.tiene_pin && (
                          <span className="text-[10px] text-amber-600">Sin PIN</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {u.ultimo_login
                        ? new Date(u.ultimo_login).toLocaleString('es-CR', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })
                        : 'Nunca'}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-xs"
                          onClick={() => setEditar(u)}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-xs"
                          onClick={() => setResetPinDe(u)}
                          title="Resetear PIN"
                        >
                          <Key className="h-4 w-4" weight="bold" aria-hidden="true" />
                        </Button>
                        {u.bloqueado && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-xs text-adelante-primary-dark"
                            onClick={() => handleDesbloquear(u)}
                            title="Desbloquear"
                          >
                            <LockOpen className="h-4 w-4" weight="bold" aria-hidden="true" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {modalCrear && <ModalCrear onClose={() => setModalCrear(false)} />}
      {editar && <ModalEditar usuario={editar} onClose={() => setEditar(null)} />}
      {resetPinDe && <ModalResetPin usuario={resetPinDe} onClose={() => setResetPinDe(null)} />}
    </div>
  );
}

// =============================================================================
// Modal: Crear usuario
// =============================================================================

function ModalCrear({ onClose }: { onClose: () => void }) {
  const crear = useCrearUsuario();
  const [nombre, setNombre] = useState('');
  const [usuario, setUsuario] = useState('');
  const [rol, setRol] = useState<RolUsuario>('campo');
  const [telefono, setTelefono] = useState('');
  const [pin, setPin] = useState('');

  async function handleCrear() {
    if (nombre.trim().length < 2 || usuario.trim().length < 2) {
      toast.error('Nombre y usuario son obligatorios');
      return;
    }
    if (!/^\d{4,6}$/.test(pin)) {
      toast.error('El PIN debe ser 4 a 6 dígitos');
      return;
    }
    try {
      await crear.mutateAsync({
        nombre: nombre.trim(),
        usuario: usuario.trim().toLowerCase(),
        rol,
        telefono: telefono.trim() || null,
        pin,
      });
      toast.success(`Usuario ${usuario} creado`);
      onClose();
    } catch (e) {
      toast.error('Error al crear', {
        description: e instanceof Error ? e.message : 'desconocido',
      });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !crear.isPending && onClose()}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" weight="bold" aria-hidden="true" />
            Nuevo usuario
          </DialogTitle>
          <DialogDescription>
            Asigná un PIN inicial — la persona puede usarlo de inmediato.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="cr-nombre" className="mb-1.5 block text-xs">
              Nombre completo
            </Label>
            <Input id="cr-nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cr-usuario" className="mb-1.5 block text-xs">
                Usuario
              </Label>
              <Input
                id="cr-usuario"
                value={usuario}
                onChange={(e) => setUsuario(e.target.value)}
                placeholder="jperez"
                className="font-mono"
              />
            </div>
            <div>
              <Label htmlFor="cr-rol" className="mb-1.5 block text-xs">
                Rol
              </Label>
              <Select value={rol} onValueChange={(v) => setRol(v as RolUsuario)}>
                <SelectTrigger id="cr-rol">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="campo">Campo</SelectItem>
                  <SelectItem value="visualizador">Visualizador</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cr-tel" className="mb-1.5 block text-xs">
                Teléfono (opcional)
              </Label>
              <Input
                id="cr-tel"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                placeholder="8888-8888"
              />
            </div>
            <div>
              <Label htmlFor="cr-pin" className="mb-1.5 block text-xs">
                PIN inicial
              </Label>
              <Input
                id="cr-pin"
                inputMode="numeric"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="4-6 dígitos"
                className="font-mono"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={crear.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleCrear} disabled={crear.isPending}>
            {crear.isPending ? 'Creando…' : 'Crear usuario'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Modal: Editar usuario
// =============================================================================

function ModalEditar({ usuario, onClose }: { usuario: UsuarioApp; onClose: () => void }) {
  const actualizar = useActualizarUsuario();
  const [nombre, setNombre] = useState(usuario.nombre);
  const [rol, setRol] = useState<RolUsuario>(usuario.rol);
  const [telefono, setTelefono] = useState(usuario.telefono ?? '');
  const [activo, setActivo] = useState(usuario.activo);

  async function handleGuardar() {
    try {
      await actualizar.mutateAsync({
        id: usuario.id,
        body: {
          nombre: nombre.trim(),
          rol,
          telefono: telefono.trim() || null,
          activo,
        },
      });
      toast.success('Usuario actualizado');
      onClose();
    } catch (e) {
      toast.error('Error', { description: e instanceof Error ? e.message : 'desconocido' });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !actualizar.isPending && onClose()}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>Editar usuario</DialogTitle>
          <DialogDescription className="font-mono text-xs">@{usuario.usuario}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="ed-u-nombre" className="mb-1.5 block text-xs">
              Nombre completo
            </Label>
            <Input id="ed-u-nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ed-u-rol" className="mb-1.5 block text-xs">
                Rol
              </Label>
              <Select value={rol} onValueChange={(v) => setRol(v as RolUsuario)}>
                <SelectTrigger id="ed-u-rol">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="campo">Campo</SelectItem>
                  <SelectItem value="visualizador">Visualizador</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="ed-u-tel" className="mb-1.5 block text-xs">
                Teléfono
              </Label>
              <Input id="ed-u-tel" value={telefono} onChange={(e) => setTelefono(e.target.value)} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} />
            Usuario activo (puede iniciar sesión)
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={actualizar.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={actualizar.isPending}>
            {actualizar.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Modal: Reset PIN
// =============================================================================

function ModalResetPin({ usuario, onClose }: { usuario: UsuarioApp; onClose: () => void }) {
  const reset = useResetPin();
  const [pin, setPin] = useState('');

  async function handleReset() {
    if (!/^\d{4,6}$/.test(pin)) {
      toast.error('El PIN debe ser 4 a 6 dígitos');
      return;
    }
    try {
      await reset.mutateAsync({ id: usuario.id, body: { pin } });
      toast.success(`PIN reseteado para ${usuario.nombre}`, {
        description: 'También se desbloqueó la cuenta si estaba bloqueada.',
      });
      onClose();
    } catch (e) {
      toast.error('Error', { description: e instanceof Error ? e.message : 'desconocido' });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !reset.isPending && onClose()}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" weight="bold" aria-hidden="true" />
            Resetear PIN
          </DialogTitle>
          <DialogDescription>
            Asignar un nuevo PIN para <strong>{usuario.nombre}</strong> (@{usuario.usuario}).
            Comunicáselo de forma segura.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor="rp-pin" className="mb-1.5 block text-xs">
            Nuevo PIN
          </Label>
          <Input
            id="rp-pin"
            inputMode="numeric"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="4-6 dígitos"
            className="font-mono text-center text-lg tracking-widest"
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={reset.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleReset} disabled={reset.isPending || !/^\d{4,6}$/.test(pin)}>
            {reset.isPending ? 'Reseteando…' : 'Resetear PIN'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
